import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './Icon';
import { IconButton } from './Button';
import { cx } from './cx';

const TONE_ICON = { info: 'info', success: 'success', warning: 'warning', danger: 'danger' };

export function Spinner({ size = 18, label = 'Загрузка', className }) {
    return <span className={cx('ds-spinner', className)} style={{ '--_s': `${size}px` }} role="status" aria-label={label} />;
}

export function Skeleton({ width = '100%', height = 12, className }) {
    return (
        <span
            className={cx('ds-skeleton', className)}
            style={{ '--_w': typeof width === 'number' ? `${width}px` : width, '--_h': `${height}px` }}
            aria-hidden="true"
        />
    );
}

// Сообщение в потоке страницы: «баланс заканчивается», «шаблон на модерации»
export function Alert({ tone = 'info', title, children, actions, onClose, className }) {
    return (
        <div className={cx('ds-alert', `ds-alert--${tone}`, className)} role={tone === 'danger' ? 'alert' : 'status'}>
            <Icon name={TONE_ICON[tone]} size={18} className="ds-alert__icon" />
            <div className="ds-alert__body">
                {title && <strong className="ds-alert__title">{title}</strong>}
                {children}
                {actions && <div className="ds-alert__actions">{actions}</div>}
            </div>
            {onClose && <IconButton icon="close" label="Закрыть" size="sm" className="ds-alert__close" onClick={onClose} />}
        </div>
    );
}

// Пустое состояние: всегда говорит, что делать дальше
export function Empty({ icon = 'inbox', title, text, action }) {
    return (
        <div className="ds-empty">
            <span className="ds-empty__icon">
                <Icon name={icon} size={22} />
            </span>
            {title && <span className="ds-empty__title">{title}</span>}
            {text && <span className="ds-empty__text">{text}</span>}
            {action && <div className="ds-empty__action">{action}</div>}
        </div>
    );
}

/* ---------- Тосты ---------- */

const ToastContext = createContext(null);
let seq = 0;

// Оборачивает приложение; toast({ tone, title, text, duration }) показывает уведомление в углу
export function ToastProvider({ children }) {
    const [items, setItems] = useState([]);
    const timers = useRef({});

    const dismiss = useCallback((id) => {
        clearTimeout(timers.current[id]);
        delete timers.current[id];
        setItems((list) => list.filter((t) => t.id !== id));
    }, []);

    const toast = useCallback(
        ({ tone = 'success', title, text, duration = 4500 }) => {
            const id = ++seq;
            setItems((list) => [...list.slice(-3), { id, tone, title, text }]);
            if (duration) timers.current[id] = setTimeout(() => dismiss(id), duration);
            return id;
        },
        [dismiss]
    );

    useEffect(() => () => Object.values(timers.current).forEach(clearTimeout), []);

    return (
        <ToastContext.Provider value={toast}>
            {children}
            {createPortal(
                <div className="ds-toasts" aria-live="polite">
                    {items.map((t) => (
                        <div key={t.id} className={cx('ds-toast', `ds-toast--${t.tone}`)} role={t.tone === 'danger' ? 'alert' : 'status'}>
                            <Icon name={TONE_ICON[t.tone] || 'success'} size={18} className="ds-toast__icon" />
                            <div className="ds-toast__body">
                                {t.title && <strong className="ds-toast__title">{t.title}</strong>}
                                {t.text && <span className="ds-toast__text">{t.text}</span>}
                            </div>
                            <IconButton icon="close" label="Закрыть" size="sm" onClick={() => dismiss(t.id)} />
                        </div>
                    ))}
                </div>,
                document.body
            )}
        </ToastContext.Provider>
    );
}

export function useToast() {
    const ctx = useContext(ToastContext);
    if (!ctx) throw new Error('useToast: оберните приложение в <ToastProvider>');
    return ctx;
}
